/**
 * Every visible string of the home page lives here (French first, ready for an
 * English version later). Rules: no em-dash or en-dash, no invented numbers,
 * one label per intent ("Démarrer un projet" is the only contact label).
 */
import projectsData from '../data/projects.json';
import { frDeep } from './typography';

export const SECTION_IDS = {
	hero: 'accueil',
	manifesto: 'manifeste',
	work: 'realisations',
	expertise: 'expertises',
	principles: 'approche',
	vision: 'vision',
	contact: 'contact',
} as const;

/** Total number of projects shown in the /globe constellation. */
export const PROJECT_COUNT = (projectsData as unknown[]).length;

export type ImageRef = { src: string; width: number; height: number; alt: string };
/** Muted, looping product demo (MP4/H.264), with a poster for reduced motion and loading. */
export type VideoRef = { src: string; poster: string; width: number; height: number; label: string };
export type LinkRef = { label: string; href: string };

export type InlineSegment =
	| { type: 'text'; value: string }
	| { type: 'image'; src: string; width: number; height: number; alt: string };

export type FeaturedProject = {
	id: string;
	title: string;
	/** Short category, e.g. "SaaS B2B". */
	kind: string;
	year: string;
	summary: string;
	stack: string[];
	/** Real screenshots. Empty when the project has none (Nanoclaw). */
	images: ImageRef[];
	links: LinkRef[];
	/** Extra facts rendered as small labels when there is no screenshot. */
	channels?: string[];
	/** Real demo recordings, shown like screenshots (Vox). */
	videos?: VideoRef[];
	/** A real command the product understands, quoted in the article. */
	command?: string;
	/** Live text visualisation rendered instead of screenshots (Brn, ex-3ngram). */
	ngram?: { sample: string; size: number; document: string };
	/** Former name, shown discreetly next to the title ("Anciennement 3ngram"). */
	formerly?: string;
	/** Maturity label when the product is not yet public ("Preuve de concept"). */
	status?: string;
	/** Product mark shown next to the title (black glyph on transparent, inverted in dark mode). */
	logo?: string;
};

/** One family of work in Réalisations, with a fixed heading. */
export type WorkGroup = {
	id: string;
	/** Two-digit index shown before the heading ("01"). */
	index: string;
	title: string;
	text: string;
	projects: FeaturedProject[];
	/** Closing line of the group, e.g. what comes next. */
	note?: string;
};

export type LogStatus = 'done' | 'doing' | 'next';
export type LogItem = { text: string; status: LogStatus };

export type DemoBeat = { id: string; scale: string; title: string; text: string };

export type Expertise = {
	id: string;
	title: string;
	short: string;
	body: string;
	examples: string[];
	stack: string[];
	/** Phosphor icon name, resolved in the section component. */
	icon: 'Sparkle' | 'ArrowsClockwise' | 'SquaresFour' | 'ShieldCheck';
};

export type Principle = {
	id: string;
	title: string;
	statement: string;
	practice: string;
};

export type Horizon = { label: string; title: string; text: string };

const content = {
	person: {
		name: 'Ozan',
		role: 'Ingénieur IA, automatisation et applications métier',
		email: 'justeozan@gmail.com',
		location: 'Auvergne-Rhône-Alpes, France et à distance',
		github: 'https://github.com/justeozan',
		lab: { name: 'Evey Labs', href: 'https://github.com/evey-run' },
	},

	nav: {
		links: [
			{ id: SECTION_IDS.work, label: 'Réalisations' },
			{ id: SECTION_IDS.expertise, label: 'Expertises' },
			{ id: SECTION_IDS.principles, label: 'Approche' },
			{ id: SECTION_IDS.vision, label: 'Vision' },
		],
		cta: 'Démarrer un projet',
		ariaLabel: 'Navigation principale',
		menuLabel: 'Menu',
		menuOpen: 'Ouvrir le menu',
		menuClose: 'Fermer le menu',
		skipLink: 'Aller au contenu',
		theme: { toDark: 'Passer en thème sombre', toLight: 'Passer en thème clair' },
	},

	hero: {
		titleLines: ['Automatiser ce qui pèse.', 'Libérer ce qui compte.'],
		subtitle:
			'Ingénieur indépendant en IA, automatisation et applications métier. Des systèmes fiables, sobres et sous contrôle humain.',
		primaryCta: 'Démarrer un projet',
		secondaryCta: 'Voir les réalisations',
	},

	manifesto: {
		title: "De la tâche à toute l'entreprise.",
		beats: [
			{
				id: 'tache',
				scale: 'Une tâche',
				title: "L'IA prépare, vous validez.",
				text: "Une demande arrive. L'agent la lit, en extrait l'essentiel et prépare la réponse. Rien ne part sans votre accord.",
			},
			{
				id: 'equipe',
				scale: 'Une équipe',
				title: "Le temps rendu revient à l'humain.",
				text: "Relances, saisies, rapports : ce qui pèse est absorbé par des workflows fiables. Le temps libéré revient aux clients, à la création, à l'équipe.",
			},
			{
				id: 'territoire',
				scale: "Toute l'entreprise",
				title: 'Vos outils reliés, vos décisions gardées.',
				text: 'E-mails, CRM, facturation, support, documents : des agents relient vos outils, repèrent ce qui compte et préparent la suite. Vous gardez la main.',
			},
		] as DemoBeat[],
		closing: 'Le progrès devrait nous rendre du temps, pas nous en prendre.',
		demo: {
			label: 'Exemple de flux automatisé',
			from: 'Atelier Mercier',
			subject: 'Demande de devis',
			body: "Bonjour, pourriez-vous chiffrer l'installation de 40 luminaires dans notre nouvel atelier à Villeurbanne ? Idéalement avant fin novembre.",
			steps: ['Demande lue', 'Informations extraites', 'Devis préparé avec votre grille tarifaire', 'En attente de votre validation'],
			extracted: [
				{ label: 'Quantité', value: '40 luminaires' },
				{ label: 'Lieu', value: 'Villeurbanne' },
				{ label: 'Délai', value: 'avant fin novembre' },
			],
			approve: "Valider l'envoi",
			approved: 'Validé par vous. Devis envoyé.',
		},
		day: {
			label: 'Exemple : une journée type',
			hours: ['8 h', '10 h', '12 h', '14 h', '16 h', '18 h'],
			chores: ['Tri des e-mails', 'Saisie CRM', 'Relances', 'Rapport', 'Devis'],
			kept: ['Rendez-vous client', 'Conception'],
			returned: ['Clients', 'Création', 'Équipe'],
			absorbedLabel: 'Pris en charge par les automatisations',
			returnedLabel: 'Temps rendu',
		},
		territory: {
			label: "Exemple : toute l'entreprise",
			sources: ['E-mails', 'CRM', 'Facturation', 'Support', 'Documents'],
			core: 'Agents IA',
			coreText: 'Relient vos outils, trient, préparent.',
			signals: ['Devis prêt à valider', 'Client à relancer aujourd’hui', 'Facture en anomalie repérée'],
			human: 'Validé par vos équipes',
		},
	},

	work: {
		eyebrow: 'Réalisations',
		title: 'Du besoin au produit en ligne.',
		intro:
			"Des systèmes d'IA, d'automatisation et de données que je conçois de bout en bout, de la source de données à l'interface. Trois familles, trois façons de travailler.",
				linkLabels: { site: 'Voir le site', code: 'Voir le code' },
		ngramLabels: { query: 'Requête', document: 'Document', similarity: 'Similarité' },
		videoLabels: { play: 'Lire la démo', pause: 'Mettre la démo en pause' },
		groups: [
			{
				id: 'erp',
				index: '01',
				title: 'ERP natifs IA, en 3D',
				text: "Des systèmes métier complets, pensés autour de l'IA dès le départ et lisibles en 3D. Volta est le premier. Les suivants naissent avec les contrats clients.",
				note: 'Le prochain sera peut-être le vôtre.',
				projects: [
					{
						id: 'energie',
						title: 'Volta',
						kind: 'Données temps réel',
						year: '2026',
						summary:
							"Le réseau électrique français en temps réel : production par filière, consommation, échanges aux frontières et intensité carbone, branchés sur l'API publique RTE éCO2mix.",
						stack: ['Next.js', 'Python', 'API RTE éCO2mix'],
						images: [
							{ src: '/img/work/energie-dashboard.webp', width: 1600, height: 1000, alt: 'Volta, vue d’ensemble : carte du réseau, consommation, intensité carbone et mix de production' },
							{ src: '/img/work/energie-graphiques.webp', width: 1600, height: 309, alt: 'Volta, courbe de charge, production par filière, historique carbone et échanges aux frontières' },
							{ src: '/img/work/energie-mobile.webp', width: 780, height: 1688, alt: 'Volta sur mobile' },
						],
						links: [
							{ label: 'Voir le site', href: 'https://energy-dashboard-red.vercel.app' },
							{ label: 'Voir le code', href: 'https://github.com/justeozan/energy-dashboard' },
						],
					},
				],
			},
			{
				id: 'suite',
				index: '02',
				title: 'La suite de travail',
				text: 'Mes propres outils, pensés comme une suite : chacun fait une chose, tous travaillent ensemble pour piloter des agents IA au quotidien.',
				projects: [
					{
						id: 'vox',
						logo: '/img/work/icons/vox.png',
						title: 'Vox',
						kind: 'La voix',
						year: '2026',
						summary:
							"Une pastille vocale pour macOS qui pilote des agents IA de développement : on lance un agent à la voix, on suit son avancement, on entend sa réponse. Tout tourne en local par défaut, en français comme en anglais.",
						stack: ['TypeScript', 'Rust', 'Whisper', 'Ollama'],
						images: [],
						videos: [
							{
								src: '/img/work/vox-demo.mp4',
								poster: '/img/work/vox-demo-poster.webp',
								width: 1280,
								height: 808,
								label: 'Vox fait le point à voix haute sur les agents en cours et recommande quoi traiter en premier',
							},
							{
								src: '/img/work/vox-pill.mp4',
								poster: '/img/work/vox-pill-poster.webp',
								width: 1440,
								height: 210,
								label: 'Une commande vocale lance un agent sur un projet',
							},
						],
						command: 'Lance un agent sur findy pour corriger les tests.',
						links: [
							{ label: 'Voir le site', href: 'https://justeozan.github.io/vox/' },
							{ label: 'Voir le code', href: 'https://github.com/justeozan/vox' },
						],
					},
					{
						id: 'nex',
						logo: '/img/work/icons/nex.png',
						title: 'Nex',
						formerly: 'Anciennement TaskFlow',
						kind: 'Le plan de travail',
						year: '2026',
						summary:
							"Un plan de travail, pas une liste de tâches : Nex lit les signaux déjà présents dans les worktrees et dans Linear, puis propose les prochaines actions, chacune expliquée.",
						stack: ['TypeScript', 'Vite', 'Git', 'Linear'],
						images: [
							{ src: '/img/work/nex.webp', width: 1440, height: 900, alt: 'Nex, le plan de travail : les prochaines actions proposées à partir des worktrees et de Linear' },
						],
						links: [{ label: 'Voir le site', href: 'https://taskflow-alpha-rouge.vercel.app' }],
					},
					{
						id: 'orb',
						logo: '/img/work/icons/orb.png',
						title: 'Orb',
						kind: 'La boucle',
						status: 'Preuve de concept',
						year: '2026',
						summary:
							"Une boucle d'agents autonome, jour et nuit : elle audite le code, ouvre des issues, les corrige dans des worktrees isolés, fait relire chaque diff par un second agent et ouvre de petites pull requests. Les décisions qui comptent restent humaines.",
						stack: ['Python', 'asyncio', 'SQLite', 'Claude Code'],
						images: [],
						channels: ['Budget et délais bornés', 'Relecture par un second agent', 'Moteurs interchangeables'],
						links: [],
					},
					{
						id: 'lrn',
						title: 'Lrn',
						kind: "L'apprentissage",
						status: 'En préparation',
						year: '2026',
						summary: "Un système d'apprentissage piloté par l'IA, pour apprendre en continu et retenir ce qui compte.",
						stack: [],
						images: [],
						links: [],
					},
					{
						id: 'brn',
						title: 'Brn',
						formerly: 'Anciennement 3ngram',
						kind: 'La mémoire',
						year: '2026',
						summary:
							"La mémoire commune de mes IA et agents : un serveur MCP qui réunit et organise ce qu'ils apprennent, avec graphe de connaissances, recherche vectorielle et recherche floue par trigrammes.",
						stack: ['TypeScript', 'Bun', 'MCP', 'SQLite'],
						images: [],
						ngram: { sample: 'automatiser les relances clients', size: 3, document: 'relance automatique des clients' },
						links: [{ label: 'Voir le site', href: 'https://3ngram.vercel.app' }],
					},
				],
			},
			{
				id: 'web',
				index: '03',
				title: 'Sites web avancés',
				text: "Des sites intelligents et nativement IA : CMS et contenu intégrés, réseaux sociaux, SEO et statistiques d'usage poussées. Le site complet, pas seulement une vitrine.",
				projects: [
					{
						id: 'lypialoc',
						title: "Lypia'loc",
						kind: 'Location de pianos',
						year: '2024',
						summary: 'Une plateforme de location courte durée qui met en relation propriétaires et locataires de pianos.',
						stack: ['React', 'Firebase', 'Tailwind CSS'],
						images: [
							{ src: '/img/work/lypialoc_home.webp', width: 1600, height: 900, alt: "Lypia'loc, page d'accueil" },
						],
						links: [],
					},
					{
						id: 'labuan',
						title: 'Labuan Bajo Explorer',
						kind: 'Tourisme',
						year: '2026',
						summary:
							"Le site d'un guide local en Indonésie : excursions en bateau vers les plus belles îles, à la demi-journée, à la journée ou au coucher du soleil, avec réservation directe sur WhatsApp.",
						stack: [],
						images: [
							{ src: '/img/work/labuan.webp', width: 1440, height: 900, alt: "Labuan Bajo Explorer, page d'accueil" },
						],
						links: [{ label: 'Voir le site', href: 'https://labuan-bajo-explorer.vercel.app' }],
					},
					{
						id: 'vmoto',
						title: 'Vmoto',
						kind: 'Concession',
						year: '2026',
						summary:
							'Le site de Soco Store, revendeur agréé Vmoto : la gamme de scooters et motos électriques, les essais sur route, le financement et l’atelier.',
						stack: [],
						images: [
							{ src: '/img/work/vmoto.webp', width: 1440, height: 900, alt: "Soco Store, revendeur Vmoto, page d'accueil" },
						],
						links: [{ label: 'Voir le site', href: 'https://justeozan.github.io/vmoto/' }],
					},
				],
			},
		] as WorkGroup[],
	},

	marquee: {
		label: "Ce que j'automatise",
		pause: 'Mettre le défilement en pause',
		play: 'Reprendre le défilement',
		items: [
			'Relances clients',
			'Devis',
			'Rapports hebdomadaires',
			'Tri des e-mails',
			'Saisie de données',
			'Synchronisation CRM',
			'Facturation',
			'Onboarding',
			'Veille',
			'Planification',
		],
	},

	expertise: {
		title: 'Quatre façons de vous rendre du temps.',
		items: [
			{
				id: 'ia',
				title: 'IA & agents',
				short: 'Des assistants qui lisent, trient et rédigent avec vous.',
				body: 'Assistants et agents connectés à vos outils et à vos documents. Ils préparent, classent et proposent. La validation reste humaine aux étapes critiques.',
				examples: [
					'Assistant interne sur vos documents',
					'Tri et réponse aux e-mails entrants',
					'Agents pilotés à la voix (Vox)',
				],
				stack: ['Claude', 'OpenAI', 'RAG', 'MCP', 'TypeScript', 'Python'],
				icon: 'Sparkle',
			},
			{
				id: 'automatisation',
				title: 'Automatisation',
				short: 'Les tâches répétitives disparaissent.',
				body: 'Relances, synchronisations, rapports, saisies : des workflows documentés, mesurés et faciles à reprendre par vos équipes.',
				examples: [
					'Synchronisation CRM et facturation',
					'Rapports générés automatiquement',
					'Relances clients et suivi des devis',
				],
				stack: ['Node.js', 'Python', 'API REST', 'Webhooks', 'Notion API'],
				icon: 'ArrowsClockwise',
			},
			{
				id: 'applications',
				title: 'Applications métier',
				short: 'Des outils sur mesure pour vos équipes.',
				body: 'CRM, tableaux de bord, portails clients, applications mobiles. Pensés avec vos équipes, conçus pour durer et évoluer.',
				examples: [
					'Tableau de bord temps réel (Volta)',
					'Portails clients et back-offices',
					'Outils internes sur mesure',
				],
				stack: ['React', 'Vue et Nuxt', 'React Native', 'Supabase', 'Firebase', 'PostgreSQL'],
				icon: 'SquaresFour',
			},
			{
				id: 'systemes',
				title: 'Systèmes & sécurité',
				short: 'Une infrastructure solide, protégée dès la conception.',
				body: "Conteneurs, déploiements automatisés, gestion des accès et des secrets. Formé à la cybersécurité, je sécurise dès la conception plutôt qu'après coup.",
				examples: [
					'Infrastructure Docker et Kubernetes',
					'Pipelines de déploiement reproductibles',
					'Audit et durcissement',
				],
				stack: ['Docker', 'Kubernetes', 'Terraform', 'Nginx', 'CI/CD'],
				icon: 'ShieldCheck',
			},
		] as Expertise[],
	},

	principles: {
		eyebrow: 'Approche',
		title: 'Quatre principes, appliqués dans le code.',
		practiceLabel: 'Concrètement',
		items: [
			{
				id: 'confidentialite',
				title: 'Confidentialité',
				statement: 'Vos données restent les vôtres.',
				practice:
					'Minimisation des données, accès cloisonnés, hébergement européen ou modèles locaux pour les informations sensibles.',
			},
			{
				id: 'controle',
				title: 'Contrôle humain',
				statement: "L'IA propose, vous décidez.",
				practice: 'Validation humaine aux étapes critiques, journal des actions, arrêt possible à tout moment.',
			},
			{
				id: 'securite',
				title: 'Sécurité',
				statement: 'Sécurisé dès la conception, pas après.',
				practice: 'Gestion des accès, secrets chiffrés, déploiements reproductibles, dépendances surveillées.',
			},
			{
				id: 'sobriete',
				title: 'Sobriété',
				statement: 'La bonne technologie, à la bonne taille.',
				practice: "J'automatise là où le gain est réel et mesuré avec vous. Nulle part ailleurs.",
			},
		] as Principle[],
	},

	vision: {
		eyebrow: 'Vision',
		titleLines: ['Une technologie qui rend la vie', 'plus douce, pas plus rapide.'],
		body: "Je crois qu'on peut confier aux machines ce qui nous épuise, pour rendre du temps au partage, à la découverte et aux passions. Des systèmes autonomes, solides et sûrs, toujours pilotés par des humains.",
		horizons: [
			{
				label: "Aujourd'hui",
				title: 'IA et automatisation',
				text: 'Des systèmes concrets pour les entreprises : utiles, sûrs et rentables.',
			},
			{
				label: 'Demain',
				title: 'Systèmes interconnectés',
				text: "Infrastructures intelligentes et jumeaux numériques à l'échelle des organisations.",
			},
			{
				label: 'À long terme',
				title: 'Villes et industries',
				text: 'Plus intelligentes, mais aussi plus libres, plus résilientes et respectueuses des personnes.',
			},
		] as Horizon[],
		log: {
			title: 'Journal de bord',
			statusLabels: { done: 'Fait', doing: 'En cours', next: 'À venir' },
			items: [
				{ text: 'Volta en ligne, le réseau électrique en temps réel', status: 'done' },
				{ text: 'Vox : piloter des agents IA à la voix', status: 'done' },
				{ text: 'Brn : une mémoire commune pour tous mes agents', status: 'done' },
				{ text: "Orb : la boucle d'agents autonome", status: 'doing' },
				{ text: "Lrn : apprendre en continu avec l'IA", status: 'doing' },
				{ text: 'Le prochain ERP natif IA, avec un client', status: 'next' },
			] as LogItem[],
		},
	},

	contact: {
		title: "Qu'est-ce qui vous prend trop de temps ?",
		text: 'Décrivez votre contexte en quelques lignes. Je reviens vers vous rapidement avec une première piste concrète.',
		directLabel: 'Ou écrivez-moi directement',
		githubLabel: 'GitHub',
		form: {
			action: 'https://formspree.io/f/mblpaqyq',
			emailSubject: 'Nouveau message depuis le site',
			next: '/?success=true#contact',
			labels: {
				name: 'Nom',
				email: 'E-mail',
				phone: 'Téléphone',
				phoneHint: 'Facultatif',
				subject: 'Objet',
				message: 'Votre besoin',
			},
			helper: {
				message: 'Contexte, outils actuels, ce que vous aimeriez automatiser.',
			},
			errors: {
				name: 'Indiquez votre nom.',
				email: 'Indiquez une adresse e-mail valide.',
				message: 'Décrivez votre besoin en quelques mots.',
			},
			subjectOptions: ['Projet IA ou agents', 'Automatisation', 'Application métier', 'Audit sécurité', 'Autre'],
			submit: 'Envoyer le message',
			sending: 'Envoi en cours',
			success: 'Merci, votre message est bien parti. Je vous réponds rapidement.',
			failure: "L'envoi n'a pas abouti. Réessayez ou écrivez-moi directement par e-mail.",
		},
	},

	footer: {
		navLabel: 'Pied de page',
		tagline: 'Automatiser ce qui pèse. Libérer ce qui compte.',
		globe: 'Constellation 3D',
		lab: 'Evey Labs',
		labText: "mon laboratoire de produits et d'open source",
		github: 'GitHub',
		rights: 'Tous droits réservés.',
	},
};

export const site = frDeep(content);
export type SiteContent = typeof site;
